import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ChartBar, History } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ParticleBackground } from '../components/ParticleBackground';
import { InterviewHistory } from '../components/common/InterviewHistory';
import { InterviewFeedbackModal } from '../components/interviews/InterviewFeedbackModal';
import { UpcomingInterviewsTab } from '../components/interviews/UpcomingInterviewsTab';
import { AnalyticsTab } from '../components/interviews/AnalyticsTab';
import { Series } from '../components/interviews/InterviewSeriesSection';
import { withPageTransition, PageTransitionItem } from '../utils/withTransition';
import { useResponsive } from '../contexts/ResponsiveContext';
import interviewService, { UserInterview } from '../services/interviewService';
import { ScheduleInterviewModal } from '../components/interviews/ScheduleInterviewModal';
import { useToast } from '../components/common/Toast';

// Date filter interface
interface DateFilter {
  value: string;
  label: string;
  fromDate?: string;
  toDate?: string;
}

// Tab types
type InterviewTab = 'upcoming' | 'history' | 'analytics';

const InterviewHistoryPage: React.FC = () => {
  const { isMobile: isMobileView } = useResponsive();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<InterviewTab>('upcoming');
  const [selectedDateFilter, setSelectedDateFilter] = useState<string>('this-month');
  const [selectedInterview, setSelectedInterview] = useState<UserInterview | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isTabChanging, setIsTabChanging] = useState(false);
  const [historyInterviews, setHistoryInterviews] = useState<UserInterview[]>([]);
  const [enrolledSeriesInterviews, setEnrolledSeriesInterviews] = useState<Series[]>([]);
  const [upcomingInterviews, setUpcomingInterviews] = useState<UserInterview[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isLoadingEnrolledSeries, setIsLoadingEnrolledSeries] = useState(false);
  const [isLoadingUpcoming, setIsLoadingUpcoming] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [enrolledSeriesError, setEnrolledSeriesError] = useState<string | null>(null);
  const [upcomingError, setUpcomingError] = useState<string | null>(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const {showToast} = useToast();
  const navigate = useNavigate();
  // Use refs to track data fetching status instead of state to prevent re-renders
  const dataFetchedRef = useRef<Record<string, boolean>>({
    upcoming: false,
    history: false,
    enrolledSeries: false
  });
  
  // Track last date filter used for historical data to prevent unnecessary fetches
  const lastDateFilterRef = useRef<string>(selectedDateFilter);
  
  // Track initial mount to prevent multiple API calls
  const isInitialMount = useRef<boolean>(true);
  
  // Track in-flight requests to prevent duplicate calls
  const pendingRequestsRef = useRef<Record<string, boolean>>({
    upcoming: false,
    history: false,
    enrolledSeries: false
  });

  // Date filters with actual date ranges
  const dateFilters: DateFilter[] = [
    { 
      value: 'this-month', 
      label: getCurrentMonthName(),
      fromDate: getDateRangeForMonth(new Date()).fromDate,
      toDate: getDateRangeForMonth(new Date()).toDate
    },
    { 
      value: 'last-month', 
      label: getLastMonthName(),
      fromDate: getDateRangeForMonth(new Date(new Date().setMonth(new Date().getMonth() - 1))).fromDate,
      toDate: getDateRangeForMonth(new Date(new Date().setMonth(new Date().getMonth() - 1))).toDate
    },
    { 
      value: 'last-3-months', 
      label: 'Last 3 Months',
      fromDate: new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString(),
      toDate: new Date().toISOString()
    },
    { 
      value: 'last-6-months', 
      label: 'Last 6 Months',
      fromDate: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString(),
      toDate: new Date().toISOString()
    },
    { 
      value: 'last-12-months', 
      label: 'Last 12 Months',
      fromDate: new Date(new Date().setMonth(new Date().getMonth() - 12)).toISOString(),
      toDate: new Date().toISOString()
    }
  ];

  // Helper function to get date range for current month
  function getDateRangeForMonth(date: Date) {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0, 23, 59, 59, 999);
    
    return {
      fromDate: firstDay.toISOString(),
      toDate: lastDay.toISOString()
    };
  }

  // Helper function to get current month name with year
  function getCurrentMonthName() {
    const date = new Date();
    return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(date);
  }

  // Helper function to get last month name with year
  function getLastMonthName() {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(date);
  }

  // Initialize the active tab from URL query param or default to 'upcoming'
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      
      // Get tab from URL or default to 'upcoming'
      const tabParam = searchParams.get('tab');
      if (tabParam && (tabParam === 'upcoming' || tabParam === 'history' || tabParam === 'analytics')) {
        setActiveTab(tabParam);
      }
      
      // Load data for the active tab on initial mount
      const initialTab = tabParam && (tabParam === 'upcoming' || tabParam === 'history' || tabParam === 'analytics')
        ? tabParam
        : 'upcoming';

      console.log(`InterviewHistoryPage: Initial load for tab ${initialTab}`);
      
      // Always load upcoming interviews on mount
      if (!dataFetchedRef.current.upcoming && !pendingRequestsRef.current.upcoming) {
        fetchUpcomingInterviews();
      }
      
      // Load history data if on history or analytics tab
      if ((initialTab === 'history' || initialTab === 'analytics') && 
          !dataFetchedRef.current.history && 
          !pendingRequestsRef.current.history) {
        fetchHistoricalInterviews();
      }
      
      // Load series data if on history or analytics tab
      if ((initialTab === 'history' || initialTab === 'analytics') && 
          !dataFetchedRef.current.enrolledSeries && 
          !pendingRequestsRef.current.enrolledSeries) {
        fetchEnrolledSeries();
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch upcoming interviews with caching
  const fetchUpcomingInterviews = async () => {
    // Don't fetch if already loaded or request is in progress
    if (dataFetchedRef.current.upcoming || pendingRequestsRef.current.upcoming) {
      console.log('InterviewHistoryPage: Skipping upcoming interviews fetch - already loaded or in progress');
      return;
    }
    
    // Mark request as in progress
    pendingRequestsRef.current.upcoming = true;
    setIsLoadingUpcoming(true);
    setUpcomingError(null);
    
    try {
      console.log('InterviewHistoryPage: Fetching upcoming interviews');
      const interviews = await interviewService.getUpcomingInterviews(true);
      setUpcomingInterviews(interviews as UserInterview[]);
      dataFetchedRef.current.upcoming = true;
    } catch (err) {
      console.error('Error fetching upcoming interviews:', err);
      setUpcomingError('Failed to load upcoming interviews. Please try again later.');
    } finally {
      setIsLoadingUpcoming(false);
      pendingRequestsRef.current.upcoming = false;
    }
  };

  // Function to fetch historical interviews with better caching
  const fetchHistoricalInterviews = async () => {
    // Don't fetch if a request is already in progress
    if (pendingRequestsRef.current.history) {
      console.log('InterviewHistoryPage: Already loading historical interviews, skipping fetch');
      return;
    }
    
    // Check if we can use cached data (already loaded and same filter)
    if (dataFetchedRef.current.history && lastDateFilterRef.current === selectedDateFilter) {
      console.log('InterviewHistoryPage: Using cached historical interviews - filter unchanged');
      return;
    }
    
    try {
      // Mark request as in progress
      pendingRequestsRef.current.history = true;
      setIsLoadingHistory(true);
      setHistoryError(null);
      
      // Get date range from the filter
      const currentFilter = dateFilters.find(filter => filter.value === selectedDateFilter) || dateFilters[0];
      console.log(`InterviewHistoryPage: Fetching interviews with date range: from=${currentFilter.fromDate}, to=${currentFilter.toDate}`);
      
      if (!currentFilter.fromDate || !currentFilter.toDate) {
        throw new Error('Invalid date range');
      }
      
      // Fetch historical interviews with date range
      const history = await interviewService.getUserInterviewHistory(
        currentFilter.fromDate,
        currentFilter.toDate
      );
      
      // Remove out the interviews where series is present and status is not_started
      const filteredHistory = history.filter((interview) => {
        if (interview.series) {
          return interview.status !== 'not_started';
        }
        return true;
      });
      setHistoryInterviews(filteredHistory);
      
      // Update tracking
      dataFetchedRef.current.history = true;
      lastDateFilterRef.current = selectedDateFilter;
    } catch (error) {
      console.error('InterviewHistoryPage: Error fetching historical interviews:', error);
      setHistoryError('Failed to load interview history');
      dataFetchedRef.current.history = false;
    } finally {
      setIsLoadingHistory(false);
      pendingRequestsRef.current.history = false;
    }
  };

  // Fetch enrolled series with better caching
  const fetchEnrolledSeries = async () => {
    // Don't fetch if already loaded or request is in progress
    if (dataFetchedRef.current.enrolledSeries || pendingRequestsRef.current.enrolledSeries) {
      console.log('InterviewHistoryPage: Skipping enrolled series fetch - already loaded or in progress');
      return;
    }
    
    // Mark request as in progress
    pendingRequestsRef.current.enrolledSeries = true;
    setIsLoadingEnrolledSeries(true);
    setEnrolledSeriesError(null);
    
    try {
      console.log('InterviewHistoryPage: Fetching enrolled series');
      const seriesData = await interviewService.getUserEnrolledSeries();
      console.log('[DEBUG] Data received from service in InterviewHistoryPage:', JSON.stringify(seriesData, null, 2));
      setEnrolledSeriesInterviews(seriesData);
      dataFetchedRef.current.enrolledSeries = true;
    } catch (err) {
      console.error('Error fetching enrolled series:', err);
      setEnrolledSeriesError('Failed to load enrolled series. Please try again later.');
      dataFetchedRef.current.enrolledSeries = false;
    } finally {
      setIsLoadingEnrolledSeries(false);
      pendingRequestsRef.current.enrolledSeries = false;
    }
  };

  // Handle view feedback with better debug logging
  const handleViewFeedback = (interviewId: string) => {
    console.log('[InterviewHistoryPage] View feedback clicked for interview:', interviewId);
    const interview = historyInterviews.find(item => item._id === interviewId);
    if (interview) {
      console.log('[InterviewHistoryPage] Found interview data for feedback:', interview);
      
      // Add debug logging for feedback data
      if (interview.feedback) {
        console.log('[InterviewHistoryPage] Interview has feedback data:', interview.feedback);
        interview.feedback.interviewDetails = {
          ...interview.interview,
          userInterviewId: interview._id,
          id: interview.interview?._id || '',
          title: interview.interview?.title || '',
          company: interview.interview?.company || '',
          jobRole: interview.interview?.role || '',
          duration: interview.interview?.durationMinutes || 30,
          description: interview.interview?.description || '',
          language: interview.interview?.language || 'English',
          tags: interview.interview?.tags || [],
          scheduledAt: interview.scheduledAt,
          newInterview: false,
          minutesUsed: interview.minutesUsed,
          userFeedbackRating: interview.userFeedbackRating
        }
      } else {
        console.log('[InterviewHistoryPage] Interview missing feedback data, using mock feedback');
        showToast('Interview feedback not generated as no interview activity was found', 'info');
      }
      
      setSelectedInterview(interview);
      setShowFeedbackModal(true);
    } else {
      console.error('[InterviewHistoryPage] Could not find interview with ID:', interviewId);
    }
  };

  // Handle rejoin interview
  const handleRejoinInterview = async (interviewId: string) => {
    try {
      const interview = historyInterviews.find(interview => interview._id === interviewId) || null;
      if (!interview) {
        console.error('Interview not found');
        showToast('Interview not found', 'error');
        return;
      }
      // Navigate to interview preview page with interview details
      navigate(`/interview-preview/${interviewId}`, {
        state: {
          interviewDetails: {
            scheduledInterviewId: interview._id, // This is the userInterviewId which means user's scheduled interview id
            id: interview.interview?._id || '',
            title: interview.interview?.title || '',
            company: interview.series ? interview.series.company : interview.interview?.company || '',
            companyLogo: interview.series ? interview.series.companyLogoUrl || interview.series.companyLogo : interview.interview?.companyLogo || '',
            companyId: interview.series ? interview.series.companyId : interview.interview?.companyId || '',
            jobRole: interview.series ? interview.series.role : interview.interview?.role || '',
            duration: interview.interview?.durationMinutes || 30,
            description: interview.interview?.description || '',
            language: interview.interview?.language || 'English',
            voicePack: interview.interview?.voicePack || '',
            characterGender: interview.interview?.characterGender || '',
            interviewPrompt: interview.interview?.interviewPrompt || '',
            isCustom: interview.isCustomInterview,
            tags: interview.interview?.tags || [],
            status: interview.status,
            createdAt: interview.createdAt,
            updatedAt: interview.updatedAt,
            newInterview: false,
            series: interview.series ? {
              id: interview.series._id,
              _id: interview.series._id,
              name: interview.series.name,
              description: interview.series.description
            } : undefined
          }
        }
      });
    } catch (err) {
      console.error('Error joining interview:', err);
      showToast('Failed to join interview. Please try again.', 'error');
    }
  };

  // Handle reschedule interview
  const handleRescheduleInterview = (interviewId: string) => {
    setSelectedInterview(historyInterviews.find(interview => interview._id === interviewId) || null);
    setShowRescheduleModal(true);
  };

  // Handle reschedule submit
  const handleRescheduleSubmit = async (date: Date, language?: string) => {
    if (!selectedInterview) return;
    
    try {
      // Call the API to reschedule the interview
      await interviewService.rescheduleInterview(
        selectedInterview._id,
        date,
        language
      );
      
      // Update the local state immediately for better UX
      const updatedInterviews = upcomingInterviews.map(interview => {
        if (interview._id === selectedInterview._id) {
          return {
            ...interview,
            scheduledAt: date.toISOString(),
            interview: {
              ...interview.interview,
              language: language || interview.interview?.language || 'English'
            }
          };
        }
        return interview;
      });
      
      setUpcomingInterviews(updatedInterviews as UserInterview[]);
      
      // Close the modal
      setShowRescheduleModal(false);
      
      // Show success message
      showToast('Interview rescheduled successfully', 'success');
      
      // Refresh data from server to ensure consistency
      fetchUpcomingInterviews();
      fetchHistoricalInterviews();
    } catch (err) {
      console.error('Error rescheduling interview:', err);
      showToast('Failed to reschedule interview. Please try again.', 'error');
    } finally {
      setIsLoadingHistory(false);
      setShowRescheduleModal(false);
    }
  };
  
  // Handle close feedback modal
  const handleCloseFeedbackModal = () => {
    setShowFeedbackModal(false);
  };
  
  // Custom background gradient styles
  const backgroundGradient = {
    background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
    position: 'relative' as 'relative',
    zIndex: 0,
    overflow: 'hidden' as 'hidden'
  };
  
  // Update the tab change handler to avoid unnecessary API calls
  const handleTabChange = (tab: InterviewTab) => {
    // Prevent tab change if one is already in progress
    if (isTabChanging) {
      console.log('InterviewHistoryPage: Tab change in progress, ignoring request');
      return;
    }
    
    // Only proceed if we're switching to a different tab
    if (tab === activeTab) {
      console.log(`InterviewHistoryPage: Already on ${tab} tab, ignoring request`);
      return;
    }
    
    console.log(`InterviewHistoryPage: Changing tab from ${activeTab} to ${tab}`);
    
    // Update URL without a page reload
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    const newUrl = `${window.location.pathname}?${newParams.toString()}`;
    window.history.replaceState(null, '', newUrl);
    
    // Set changing tabs state to true
    setIsTabChanging(true);
    
    // Update active tab state
    setActiveTab(tab);
    
    // Check if we need to load data for the new tab - only if we haven't loaded it yet
    if (tab === 'upcoming' && !dataFetchedRef.current.upcoming && !pendingRequestsRef.current.upcoming) {
      fetchUpcomingInterviews();
    }
    else if ((tab === 'history' || tab === 'analytics')) {
      if (!dataFetchedRef.current.history && !pendingRequestsRef.current.history) {
        fetchHistoricalInterviews();
      }
      
      if (!dataFetchedRef.current.enrolledSeries && !pendingRequestsRef.current.enrolledSeries) {
        fetchEnrolledSeries();
      }
    }
  };

  // Shorter tab labels for mobile
  const tabLabels = {
    upcoming: isMobileView ? 'Upcoming' : 'Upcoming Interviews',
    history: isMobileView ? 'History' : 'Interview History',
    analytics: isMobileView ? 'Analytics' : 'Performance Analytics'
  };

  // Render the history content
  const renderHistoryContent = () => {
    // Separate loading states for series and interview history
    const isSeriesLoading = isLoadingEnrolledSeries;
    const isHistoryLoading = isLoadingHistory;
    const hasSeriesError = enrolledSeriesError;
    const hasHistoryError = historyError;
    
    console.log('[DEBUG] renderHistoryContent - series data:', JSON.stringify(enrolledSeriesInterviews, null, 2));
    console.log('[DEBUG] renderHistoryContent - isLoading:', isSeriesLoading);

    return (
      <div className="space-y-8">
        {/* Series section with its own loading state */}
       { hasSeriesError ? (
          <div className="text-center py-6 text-red-500 p-4 border rounded-lg border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark">
            <p>{enrolledSeriesError}</p>
            <button 
              className="mt-4 px-4 py-2 bg-primary-light dark:bg-primary-dark text-white rounded-md hover:bg-primary-light/90 dark:hover:bg-primary-dark/90 transition"
              onClick={fetchEnrolledSeries}
            >
              Retry
            </button>
          </div>
        ) : (
          // Commenteed out for now - We're not showing Browse All Series in Navbar
          <div>
            {/* <InterviewSeriesSection 
              isLoading={isSeriesLoading}
              series={enrolledSeriesInterviews}
              isSeriesData={true}
            /> */}
          </div>
        )}
        
        {/* Interview history section with its own loading state */}
         {hasHistoryError ? (
          <div className="text-center py-6 text-red-500 p-4 border rounded-lg border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark">
            <p>{historyError}</p>
            <button 
              className="mt-4 px-4 py-2 bg-primary-light dark:bg-primary-dark text-white rounded-md hover:bg-primary-light/90 dark:hover:bg-primary-dark/90 transition"
              onClick={fetchHistoricalInterviews}
            >
              Retry
            </button>
          </div>
        ) : (
          <InterviewHistory 
            interviews={historyInterviews}
            dateFilters={dateFilters}
            selectedDateFilter={selectedDateFilter}
            onDateFilterChange={setSelectedDateFilter}
            onViewFeedback={handleViewFeedback}
            onRejoinInterview={handleRejoinInterview}
            onRescheduleInterview={handleRescheduleInterview}
            isLoading={isHistoryLoading}
            error={historyError}
            onRetry={fetchHistoricalInterviews}
          />
        )}
      </div>
    );
  };

  // Handle date filter change
  const handleDateFilterChange = (filterValue: string) => {
    // Only fetch new data if the filter actually changed
    if (filterValue !== selectedDateFilter) {
      console.log(`InterviewHistoryPage: Date filter changed from ${selectedDateFilter} to ${filterValue}`);
      setSelectedDateFilter(filterValue);
      
      // The fetchHistoricalInterviews will be called by the useEffect below
    }
  };

  // Fetch history interviews when date filter changes
  useEffect(() => {
    // Skip on initial mount as this will be handled in the first useEffect
    if (isInitialMount.current) {
      return;
    }
    
    // Only fetch if:
    // 1. We're on a tab that shows historical data
    // 2. The date filter changed (tracked through lastDateFilterRef)
    // 3. We're not already loading
    // 4. We're not in the middle of a tab change
    if ((activeTab === 'history' || activeTab === 'analytics') &&
        selectedDateFilter !== lastDateFilterRef.current &&
        !pendingRequestsRef.current.history &&
        !isTabChanging) {
      console.log(`InterviewHistoryPage: Date filter changed from ${lastDateFilterRef.current} to ${selectedDateFilter}, fetching new historical data`);
      fetchHistoricalInterviews();
    }
  }, [selectedDateFilter, activeTab, isTabChanging]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect to update state when tab changes
  useEffect(() => {
    if (isTabChanging) {
      console.log(`InterviewHistoryPage: Tab change complete to ${activeTab}`);
      
      // Important: force data load if switching to history/analytics and data hasn't been loaded yet
      if ((activeTab === 'history' || activeTab === 'analytics') && 
          !dataFetchedRef.current.history && 
          !pendingRequestsRef.current.history) {
        console.log('InterviewHistoryPage: Forcing historical data load on tab change because data not loaded yet');
        fetchHistoricalInterviews();
      }
      
      if ((activeTab === 'history' || activeTab === 'analytics') && 
          !dataFetchedRef.current.enrolledSeries && 
          !pendingRequestsRef.current.enrolledSeries) {
        console.log('InterviewHistoryPage: Forcing enrolled series load on tab change because data not loaded yet');
        fetchEnrolledSeries();
      }
      
      setIsTabChanging(false);
    }
  }, [activeTab, isTabChanging]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark" style={backgroundGradient}>
      {/* Animated gradient elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <ParticleBackground />
      
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="max-w-7xl mx-auto">
          <PageTransitionItem index={1}>
            <h1 className="text-2xl md:text-3xl mt-6 font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
              My Interviews
            </h1>
            
            <p className="text-text-secondary-light dark:text-text-secondary-dark mb-8 max-w-3xl font-medium text-base">
              View your scheduled interviews, track your progress, and analyze your performance
            </p>
          </PageTransitionItem>

          {/* Tabs with mobile optimization */}
          <PageTransitionItem index={2}>
            <div className="overflow-x-auto md:overflow-visible pb-2 md:pb-0">
              <div className="inline-flex w-full md:w-auto mb-8 bg-surface-light/50 dark:bg-surface-dark/50 backdrop-blur-sm p-1 rounded-lg min-w-full md:min-w-0">
                <button
                  onClick={() => handleTabChange('upcoming')}
                  className={`flex-1 md:flex-none px-3 md:px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    activeTab === 'upcoming'
                      ? 'bg-primary-light dark:bg-primary-dark text-white'
                      : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-background-light dark:hover:bg-background-dark'
                  }`}
                >
                  <div className="flex items-center justify-center md:justify-start">
                    <Calendar size={16} className="mr-1.5 flex-shrink-0" />
                    <span className="truncate">{tabLabels.upcoming}</span>
                  </div>
                </button>
                <button
                  onClick={() => handleTabChange('history')}
                  className={`flex-1 md:flex-none px-3 md:px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    activeTab === 'history'
                      ? 'bg-primary-light dark:bg-primary-dark text-white'
                      : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-background-light dark:hover:bg-background-dark'
                  }`}
                >
                  <div className="flex items-center justify-center md:justify-start">
                    <History size={16} className="mr-1.5 flex-shrink-0" />
                    <span className="truncate">{tabLabels.history}</span>
                  </div>
                </button>
                <button
                  onClick={() => handleTabChange('analytics')}
                  className={`flex-1 md:flex-none px-3 md:px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    activeTab === 'analytics'
                      ? 'bg-primary-light dark:bg-primary-dark text-white'
                      : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-background-light dark:hover:bg-background-dark'
                  }`}
                >
                  <div className="flex items-center justify-center md:justify-start">
                    <ChartBar size={16} className="mr-1.5 flex-shrink-0" />
                    <span className="truncate">{tabLabels.analytics}</span>
                  </div>
                </button>
              </div>
            </div>
          </PageTransitionItem>

          {/* Tab Content */}
          <PageTransitionItem index={3}>
            <div>
              {activeTab === 'upcoming' && (
                <UpcomingInterviewsTab 
                  interviews={upcomingInterviews}
                  isLoading={isLoadingUpcoming}
                  error={upcomingError}
                  onRetry={fetchUpcomingInterviews}
                />
              )}
              {activeTab === 'history' && renderHistoryContent()}
              {activeTab === 'analytics' && (
                <AnalyticsTab 
                  interviews={historyInterviews}
                  dateFilters={dateFilters}
                  selectedDateFilter={selectedDateFilter}
                  onDateFilterChange={handleDateFilterChange}
                  isLoading={isLoadingHistory}
                  error={historyError}
                  onRetry={fetchHistoricalInterviews}
                />
              )}
            </div>
          </PageTransitionItem>
        </div>
      </main>

      {/* Interview Feedback Modal - Rendered at page level */}
      {selectedInterview && showFeedbackModal && selectedInterview.feedback && (
        <InterviewFeedbackModal
          isOpen={showFeedbackModal}
          onClose={handleCloseFeedbackModal}
          feedback={selectedInterview.feedback}
        />
      )}

      {/* Reschedule Modal */}
      {selectedInterview && showRescheduleModal && (
        <ScheduleInterviewModal
          isOpen={showRescheduleModal}
          onClose={() => setShowRescheduleModal(false)}
          interviewDetails={{
            id: selectedInterview.interview?._id || '',
            title: selectedInterview.interview?.title || '',
            description: selectedInterview.interview?.description || '',
            company: selectedInterview.interview?.company || '',
            jobRole: selectedInterview.interview?.role || '',
            language: selectedInterview.interview?.language || 'English',
            duration: selectedInterview.interview?.durationMinutes || 30,
          }}
          initialDate={selectedInterview.scheduledAt}
          mode="reschedule"
          onSchedule={handleRescheduleSubmit}
        />
      )}
      
    </div>
  );
};

InterviewHistoryPage.displayName = 'InterviewHistoryPage';

// Export with page transition HOC
export default withPageTransition(InterviewHistoryPage); 